/** @prettier */

import {Grid, Card, CardContent, Stack, Box, Container} from '@mui/material';
import {LoaderFunctionArgs, useLoaderData} from 'react-router-dom';
import {Fragment, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import IndoorUnitDemandControlDialog from './IndoorUnitDemandControlDialog';
import OutdoorUnitDemandControlDialog from './OutdoorUnitDemandControlDialog';
import CardContents from '../../component/CardContents';
//interface import
import * as apiIF from '../../Type/api';
//API import
import {
  getDemandSetting,
  getCompositionsGateway,
  getCompositionsSitesBySiteID,
} from '../../API/API_Collection';
//Style import
import '../../css/common/common.css';

import {iconBlack, limit} from '../../define';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';

type LoaderReturnType = {
  allDemandInfo: apiIF.res.getDemandSetting; //デマンド情報
  gatewayInfo: apiIF.res.getCompositionsGateway; //ゲートウェイの情報
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //デマンドの情報を取得
    promises.push(getDemandSetting(params.siteID));
    //ゲートウェイの情報を取得
    promises.push(
      getCompositionsGateway(params.siteID, {
        'fields': 'gateway-name,demand-level,system',
        'max-result': limit.REGISTRATION_NUM_GW_MAX,
      })
    );
    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //全てのユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));

    const response = await Promise.all(promises);
    const siteInfo = response[2] as apiIF.res.getCompositionsSitesBySiteID;
    const unitExistence = response[3] as unitExistence;

    if (
      unitExistence.hasAirconIU === false ||
      siteInfo['site-authority'] !== 'admin'
    ) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    return {
      allDemandInfo: response[0] as apiIF.res.getDemandSetting,
      gatewayInfo: response[1] as apiIF.res.getCompositionsGateway,
    };
  } catch {
    return undefined;
  }
}

/**
 * デマンド設定のホーム画面
 * @returns 表示画面
 */
const DemandSetting = () => {
  //loaderのデータを取得
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  //デマンド情報取得用
  const [allDemandInfo, setAllDemandInfo] = useState<
    apiIF.res.getDemandSetting | undefined
  >();

  useEffect(() => {
    if (loaderData !== undefined) {
      setAllDemandInfo(loaderData.allDemandInfo);
    }
  }, [loaderData]);

  const trans = useTranslation().t; //翻訳関数

  /**各レベルに対応する表示 */
  const levelDisp = {
    '': trans('DemandControl.card.noSignal'),
    'level1': trans('DemandControl.card.level1'),
    'level2': trans('DemandControl.card.level2'),
    'level3': trans('DemandControl.card.level3'),
    'level0': trans('DemandControl.card.level0'),
  };

  /**
   * ゲートウェイのデマンドレベルを取得する関数
   * @returns ゲートウェイのデマンドレベル
   */
  const gatewayLevel = (() => {
    /**
     * 全てのゲートウェイのデマンドレベルを見て、デマンドコントローラーに接続されている
     * ゲートウェイの情報を取得する。
     * RT通信はデマンド対象外の為、フィルタリングを実施
     * デマンドコントローラーに接続されているゲートウェイは１個のみなので
     * 一度level0, level1, level2, level3のいずれかが検出された場合、そのデマンドレベルを持つ
     * ゲートウェイの情報を返す
     */
    if (loaderData === undefined) {
      return [];
    }
    const targetGatewayInfo = loaderData.gatewayInfo['gateway-informations']
      // SL通信設定のGWのみ対象とする
      .filter(gwInfo => gwInfo['system'] === 'sl')
      .map(gwInfo => {
        return {
          gwName: gwInfo['gateway-name'],
          level: gwInfo['demand-level'],
        };
      });

    return targetGatewayInfo;
  })();

  return (
    <Container fixed>
      <Grid container rowSpacing={2}>
        {/** タイトル、説明 */}
        <Grid item xs={12}>
          <h1>{trans('DemandControl.title')}</h1>
          <h2>{trans('DemandControl.description.limit')}</h2>
          <div className="demandSetting__contents_caution">
            {trans('DemandControl.note')}
          </div>
          <div className="demandSetting__contents_caution">
            {'* ' + trans('DemandControl.description.notChanged')}
          </div>
          <div className="demandSetting__contents_caution">
            {'* ' + trans('DemandControl.description.notRemovedGroup')}
          </div>
        </Grid>
        {/** 設定値 */}
        <Grid item xs={12} container justifyContent="flex" alignItems="center">
          {/** サイト名 */}
          {allDemandInfo === undefined ? (
            <></>
          ) : (
            <Grid item xs={12}>
              <h2>{allDemandInfo['site-name']}</h2>
            </Grid>
          )}
          {/** 現在のデマンドレベル */}
          <Grid item xs={6}>
            <div className="demandSetting__contents__currentLevel">
              <Box className="demandSetting__contents__currentLevel__title">
                {trans('DemandControl.currentLevel.item')}
              </Box>
              {gatewayLevel.length === 0 ? (
                <span className="demandSetting__contents__currentLevel__value">
                  {trans('DemandControl.currentLevel.error')}
                </span>
              ) : (
                <Grid
                  container
                  className="demandSetting__contents__currentLevel__value">
                  {gatewayLevel.map((gwInfo, index) => {
                    return (
                      <Fragment key={`gatewayLevel-gw${index}`}>
                        <Grid item xs={8}>
                          {gwInfo['gwName']}
                        </Grid>
                        <Grid item xs={4}>
                          :&nbsp;
                          <span className="demandSetting__contents__currentLevel__value_level">
                            {gwInfo['level'] !== undefined &&
                              levelDisp[gwInfo['level']]}
                          </span>
                        </Grid>
                      </Fragment>
                    );
                  })}
                </Grid>
              )}
            </div>
          </Grid>
          {/* IndoorとOutdoorのユニットのデマンド制御の設定およびデマンドレベル別のユニット数の表示 */}
          {allDemandInfo !== undefined ? (
            <DemandInfo
              allDemandInfo={allDemandInfo}
              setAllDemandInfo={setAllDemandInfo}
            />
          ) : (
            <h3>{trans('DemandControl.card.noInfo')}</h3>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

/**
 * 室内機・室外機の各デマンドレベル別のユニット数および設定ボタンを表示
 * @param props
 * @returns 表示画面
 */
const DemandInfo = (props: {
  allDemandInfo: apiIF.res.getDemandSetting; //デマンド情報
  setAllDemandInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getDemandSetting | undefined> //デマンド情報の設定関数
  >;
}) => {
  const [isOpenIndoorDialog, setIsOpenIndoorDialog] = useState(false); //室内機の編集ダイアログ表示フラグ
  const [isOpenOutdoorDialog, setIsOpenOutdoorDialog] = useState(false); //室外機の編集ダイアログ表示フラグ
  //loaderのデータを取得
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  /**
   * 各デマンドレベルのユニット数
   */
  const [unitCount, setUnitCount] = useState({
    ou: {lv0: 0, lv1: 0, lv2: 0, lv3: 0, all: 0},
    iu: {lv0: 0, lv1: 0, lv2: 0, lv3: 0, all: 0},
  });

  /**
   * Editボタン押下時処理
   * @param unitType 室内機or室外機
   */
  const openEditDialog = (unitType: 'Indoor' | 'Outdoor') => {
    setIsOpenIndoorDialog(unitType === 'Indoor');
    setIsOpenOutdoorDialog(unitType === 'Outdoor');
  };

  const trans = useTranslation().t; //翻訳関数

  /**
   * 歯車メニューリスト
   * @param unitType 室内機or室外機
   * @returns メニュー一覧
   */
  const gearMenuItem = (
    unitType: 'Indoor' | 'Outdoor'
  ): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: `mv-demandEdit${unitType}`,
        label: trans('DemandControl.card.gearMenuItem.edit'),
        onClick: () => openEditDialog(unitType),
        icon: {
          className: 'demandSetting__contents__card__header__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `DemandControl.image.gearMenu.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      },
    ];
  };

  /**有効/無効に対応する表示 */
  const activeDisp = {
    active: trans('DemandControl.card.active'),
    inactive: trans('DemandControl.card.inactive'),
  };

  /** 制御対象ユニット台数表示 */
  const unitCountPerDemandLevel = (unitCount: number, allUnitCount: number) => {
    return (
      <Grid container>
        <Grid item xs={5}>
          <div className="demandSetting__contents__card__unitCount__contents__eachLevelContent__countNum">
            {trans('DemandControl.card.unit', {
              unitCount: unitCount,
              allUnitCount: allUnitCount,
            })}
          </div>
        </Grid>
      </Grid>
    );
  };

  /**
   * ゲートウェイのデマンドレベルを取得する関数
   * @returns ゲートウェイのデマンドレベル
   */
  const gatewayLevel = (() => {
    /**
     * 全てのゲートウェイのデマンドレベルを見て、デマンドコントローラーに接続されている
     * ゲートウェイの情報を取得する。
     * RT通信はデマンド対象外の為、フィルタリングを実施
     * デマンドコントローラーに接続されているゲートウェイは１個のみなので
     * 一度level0, level1, level2, level3のいずれかが検出された場合、そのデマンドレベルを持つ
     * ゲートウェイの情報を返す
     */
    if (loaderData === undefined) {
      return [];
    }
    const targetGatewayInfo = loaderData.gatewayInfo['gateway-informations']
      // SL通信設定のGWのみ対象とする
      .filter(gwInfo => gwInfo['system'] === 'sl')
      .map(gwInfo => {
        return {
          gwName: gwInfo['gateway-name'],
          level: gwInfo['demand-level'],
        };
      });

    return targetGatewayInfo;
  })();

  /***
   * Gatewayのデマンド実施状況を返す関数 (SL通信デマンドのみ対象)
   * @param gatewayLevel デマンドレベル入力
   * @return demandImplementation boolean  ture(実施中) | false(未実施)
   */
  const judgeDemandImplementation = (
    gatewayLevel: {
      gwName: string | undefined;
      level: '' | 'level0' | 'level1' | 'level2' | 'level3' | undefined;
    }[]
  ) => {
    /** デマンド実施可否 */
    // 少なくとも1つのGWにLevel1,2,3いずれか設定されていればTrue
    const demandImplementation = gatewayLevel.some(
      gwData =>
        gwData.level === 'level1' ||
        gwData.level === 'level2' ||
        gwData.level === 'level3'
    );
    return demandImplementation;
  };

  return (
    <Grid container>
      <Grid item xs={12} lg={6}>
        <Card className="demandSetting__contents__card">
          {/* 室内機ユニット */}
          <CardContent>
            <Stack spacing={2}>
              {/* セクション名と歯車 */}
              <Grid
                container
                justifyContent="space-between"
                className="demandSetting__contents__card__header">
                {/* セクション名 */}
                <Grid
                  item
                  xs="auto"
                  className="demandSetting__contents__card__header__title">
                  {trans('DemandControl.card.inUnitHeader')}
                </Grid>
                {/* 歯車ボタン */}
                <Grid item xs={2}>
                  <GearMenu
                    id={`mv-btnDemandSettingOpenIndoor`}
                    menuItem={gearMenuItem('Indoor')}
                  />
                  <IndoorUnitDemandControlDialog
                    allDemandInfo={props.allDemandInfo}
                    isOpenIndoorDialog={isOpenIndoorDialog}
                    setIsOpenIndoorDialog={setIsOpenIndoorDialog}
                    setAllDemandInfo={props.setAllDemandInfo}
                    setUnitCount={setUnitCount}
                    demandImplementation={judgeDemandImplementation(
                      gatewayLevel
                    )}
                  />
                </Grid>
              </Grid>
              <Stack spacing={2}>
                {/* モードの表示 */}
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={3}
                    className="demandSetting__contents__card__mode__title">
                    {trans('DemandControl.card.mode')}
                  </Grid>
                  <Grid item xs={2}>
                    :&nbsp;
                    <Box
                      component="span"
                      className="demandSetting__contents__card__mode__setting">
                      {activeDisp[props.allDemandInfo['demand']['indoor-unit']]}
                    </Box>
                  </Grid>
                </Grid>
                {/* セクション名*/}
                <Box className="demandSetting__contents__card__unitCount">
                  <Box
                    justifyContent="left"
                    className="demandSetting__contents__card__unitCount__title">
                    {trans('DemandControl.card.controlledUnits')}
                  </Box>
                  {/* デマンドレベル毎の室内機の数 */}
                  <Stack
                    spacing={2}
                    className="demandSetting__contents__card__unitCount__contents">
                    {/* レベル１ */}
                    <CardContents
                      class="demandSetting__contents__card__unitCount__contents__eachLevelContent"
                      valueDescription={trans('DemandControl.card.level1')}
                      value={unitCountPerDemandLevel(
                        unitCount.iu.lv1,
                        unitCount.iu.all
                      )}
                    />
                    {/* レベル２ */}
                    <CardContents
                      class="demandSetting__contents__card__unitCount__contents__eachLevelContent"
                      valueDescription={trans('DemandControl.card.level2')}
                      value={unitCountPerDemandLevel(
                        unitCount.iu.lv2,
                        unitCount.iu.all
                      )}
                    />
                    {/* レベル３ */}
                    <CardContents
                      class="demandSetting__contents__card__unitCount__contents__eachLevelContent"
                      valueDescription={trans('DemandControl.card.level3')}
                      value={unitCountPerDemandLevel(
                        unitCount.iu.lv3,
                        unitCount.iu.all
                      )}
                    />
                    {/* None */}
                    <CardContents
                      class="demandSetting__contents__card__unitCount__contents__eachLevelContent"
                      valueDescription={trans('DemandControl.card.level0')}
                      value={unitCountPerDemandLevel(
                        unitCount.iu.lv0,
                        unitCount.iu.all
                      )}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} lg={6}>
        <Card className="demandSetting__contents__card">
          {/* 室外機ユニット */}
          <CardContent>
            <Stack spacing={2}>
              {/* セクション名と歯車 */}
              <Grid
                container
                justifyContent="space-between"
                className="demandSetting__contents__card__header">
                {/* セクション名 */}
                <Grid
                  item
                  xs="auto"
                  className="demandSetting__contents__card__header__title">
                  {trans('DemandControl.card.outUnitHeader')}
                </Grid>
                {/* 歯車ボタン */}
                <Grid item xs={2}>
                  <GearMenu
                    id={`mv-btnDemandSettingOpenOutdoor`}
                    menuItem={gearMenuItem('Outdoor')}
                  />
                  <OutdoorUnitDemandControlDialog
                    allDemandInfo={props.allDemandInfo}
                    isOpenOutdoorDialog={isOpenOutdoorDialog}
                    setIsOpenOutdoorDialog={setIsOpenOutdoorDialog}
                    setAllDemandInfo={props.setAllDemandInfo}
                    setUnitCount={setUnitCount}
                    demandImplementation={judgeDemandImplementation(
                      gatewayLevel
                    )}
                  />
                </Grid>
              </Grid>
              <Stack spacing={2}>
                {/* モードの表示 */}
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={3}
                    className="demandSetting__contents__card__mode__title">
                    {trans('DemandControl.card.mode')}
                  </Grid>
                  <Grid item xs={2}>
                    :&nbsp;
                    <Box
                      component="span"
                      className="demandSetting__contents__card__mode__setting">
                      {
                        activeDisp[
                          props.allDemandInfo['demand']['outdoor-unit']
                        ]
                      }
                    </Box>
                  </Grid>
                </Grid>
                {/* セクション名 */}
                <Box className="demandSetting__contents__card__unitCount">
                  <Box
                    justifyContent="left"
                    className="demandSetting__contents__card__unitCount__title">
                    {trans('DemandControl.card.controlledUnits')}
                  </Box>

                  {/* デマンドレベル毎の室外機の数 */}
                  <Stack
                    spacing={2}
                    className="demandSetting__contents__card__unitCount__contents">
                    {/* レベル１ */}
                    <CardContents
                      class="demandSetting__contents__card__unitCount__contents__eachLevelContent"
                      valueDescription={trans('DemandControl.card.level1')}
                      value={unitCountPerDemandLevel(
                        unitCount.ou.lv1,
                        unitCount.ou.all
                      )}
                    />
                    {/* レベル２ */}
                    <CardContents
                      class="demandSetting__contents__card__unitCount__contents__eachLevelContent"
                      valueDescription={trans('DemandControl.card.level2')}
                      value={unitCountPerDemandLevel(
                        unitCount.ou.lv2,
                        unitCount.ou.all
                      )}
                    />
                    {/* レベル３ */}
                    <CardContents
                      class="demandSetting__contents__card__unitCount__contents__eachLevelContent"
                      valueDescription={trans('DemandControl.card.level3')}
                      value={unitCountPerDemandLevel(
                        unitCount.ou.lv3,
                        unitCount.ou.all
                      )}
                    />
                    {/* None */}
                    <CardContents
                      class="demandSetting__contents__card__unitCount__contents__eachLevelContent"
                      valueDescription={trans('DemandControl.card.level0')}
                      value={unitCountPerDemandLevel(
                        unitCount.ou.lv0,
                        unitCount.ou.all
                      )}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DemandSetting;
